import SaveIcon from '@mui/icons-material/Save';
import { Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import { useMemo, useState } from 'react';

import { ApplicationDeadlineAdminProps } from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';

type ApplicationDeadlineActionsProps = {
  params: GridRenderCellParams<ApplicationDeadlineAdminProps>;
  touchedRowKeys: string[];
  handleSave: (row: ApplicationDeadlineAdminProps) => Promise<void>;
};

const ACTION_BUTTON_SIZE = 28;

const ApplicationDeadlineActions = ({
  params,
  touchedRowKeys,
  handleSave,
}: ApplicationDeadlineActionsProps) => {
  const [loading, setLoading] = useState(false);
  const rowKey = String(params.id);

  const hasUnsavedChanges = useMemo(() => {
    return touchedRowKeys.includes(rowKey);
  }, [rowKey, touchedRowKeys]);

  const actionsDisabled = useMemo(() => {
    return loading || !hasUnsavedChanges;
  }, [hasUnsavedChanges, loading]);

  const onSave = async () => {
    setLoading(true);
    try {
      await handleSave(params.row);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {loading ? (
        <CircularProgress size={ACTION_BUTTON_SIZE} sx={{ color: 'green' }} />
      ) : (
        <Tooltip title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}>
          <span>
            <IconButton
              aria-label='save application deadline changes'
              disabled={actionsDisabled}
              onClick={() => {
                void onSave();
              }}
              sx={{
                maxWidth: ACTION_BUTTON_SIZE,
                minWidth: ACTION_BUTTON_SIZE,
                maxHeight: ACTION_BUTTON_SIZE,
                minHeight: ACTION_BUTTON_SIZE,
                color: hasUnsavedChanges ? 'warning.main' : 'action.disabled',
              }}
            >
              <SaveIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default ApplicationDeadlineActions;
